import { Box, Typography, useTheme } from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { stateWasteGeneratorStatusToLabel } from "encamp-shared/src/waste";

/**
 * State-status label for the discrepancy displays. A missing status reads
 * "None calculated" — distinct from Not a Generator, which is a real reported/
 * calculated value.
 */
export function stateStatusPillLabel(status: string | null): string {
  return status ? stateWasteGeneratorStatusToLabel(status) : "None calculated";
}

const STATUS_RANK: Record<string, number> = {
  VERY_SMALL_QUANTITY_GENERATOR: 1,
  SMALL_QUANTITY_GENERATOR: 2,
  LARGE_QUANTITY_GENERATOR: 3,
};

function getBgColor({
  escalated,
  downgraded,
  theme,
}: {
  escalated: boolean;
  downgraded: boolean;
  theme: Theme;
}): string {
  const isLight = theme.palette.mode === "light";
  if (escalated) return alpha(theme.palette.error.main, isLight ? 0.06 : 0.08);
  if (downgraded) return alpha(theme.palette.success.main, isLight ? 0.06 : 0.08);
  return alpha(theme.palette.warning.main, isLight ? 0.06 : 0.08);
}

/**
 * The old → new status pill shared by the grid's Discrepancy columns and the
 * drawer's finding rows: tinted red for an escalation, green for a downgrade,
 * orange for a sideways move; plain text when nothing changed.
 */
export function StatusTransition({
  oldStatus,
  oldStatusLabel,
  newStatus,
  rank = (status) => STATUS_RANK[status] ?? 0,
  label = (status) => (status ? prettyPrintEnumValue(status) : "—"),
}: {
  oldStatus: string | null | undefined;
  /** Replaces the old-status text only; ranking still uses oldStatus. */
  oldStatusLabel?: string | null;
  newStatus: string | null | undefined;
  /** Escalation/downgrade ordering; defaults to the federal tier ladder. */
  rank?: (status: string) => number;
  /** Also called for a missing status, so it can say "None" instead of a dash. */
  label?: (status: string | null) => string;
}) {
  const theme = useTheme();
  const changed = oldStatus !== newStatus;
  const oldRank = oldStatus ? rank(oldStatus) : 0;
  const newRank = newStatus ? rank(newStatus) : 0;
  const escalated = changed && newRank > oldRank;
  const downgraded = changed && newRank < oldRank;

  const oldLabel = oldStatusLabel ?? label(oldStatus ?? null);
  const newLabel = label(newStatus ?? null);

  if (!changed) {
    return (
      <Typography variant="body2" color="text.secondary" noWrap>
        {newLabel}
      </Typography>
    );
  }

  const bgColor = getBgColor({ escalated, downgraded, theme });

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        backgroundColor: bgColor,
        borderRadius: 1,
        px: 0.75,
        py: 0.25,
        maxWidth: "100%",
      }}
    >
      <Typography variant="body2" noWrap>
        {oldLabel}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        →
      </Typography>
      <Typography variant="body2" fontWeight={600} noWrap>
        {newLabel}
      </Typography>
    </Box>
  );
}
